import { AddTaskFormData, SimulatorConfigFormData } from '../../../interfaces/customer-simulator';
import { Button, Flex, Form, Input, ModalManagerContext, TextArea } from '@pega/cosmos-react-core';
import React, { useContext } from 'react';
import { useForm } from 'react-hook-form';

import useLocalTranslation from '../../../hooks/useLocalTranslation';
import CustomLabel from '../../Utilities/CustomLabel';

interface AddTaskProps {
  handleFormSubmit: (data: SimulatorConfigFormData) => void;
  scenario?: SimulatorConfigFormData;
}

const AddTask: React.FC<AddTaskProps> = ({ handleFormSubmit, scenario }) => {
  const { register, handleSubmit, errors, formState } = useForm<AddTaskFormData>({
    mode: 'all',
    defaultValues: {
      simulationScenario: scenario?.simulationScenario || '',
      queueName: scenario?.queueName || '',
      personality: scenario?.personality || '',
      painPoints: scenario?.painPoints || '',
    },
  });
  const translate = useLocalTranslation();
  const { ModalContext } = useContext(ModalManagerContext);
  const { dismiss } = useContext(ModalContext);

  const addRecord = (data: SimulatorConfigFormData) => {
    const isValidData = data.simulationScenario?.trim();

    if (formState.isValid && isValidData) {
      if (scenario) {
        handleFormSubmit({ ...data, taskId: scenario.taskId });
      } else {
        handleFormSubmit(data);
      }
      dismiss();
    }
  };

  const handleOnSubmit = (event: any) => {
    event.preventDefault();
    handleSubmit(addRecord)();
  };

  const actions = (
    <>
      <Button onClick={dismiss}>{translate('cancel')}</Button>
      <Button variant="primary" disabled={!formState.isValid} onClick={handleOnSubmit}>
        {translate(scenario ? 'update' : 'add')}
      </Button>
    </>
  );

  return (
    <Form actions={actions}>
      <Flex
        container={{
          colGap: 2,
          direction: 'column',
          gap: 2,
        }}
      >
        <Flex
          container={{
            direction: 'column',
            gap: 1,
          }}
        >
          <CustomLabel
            label={translate('simulation_scenario')}
            showRequired={true}
            tooltipInfo={translate('simulation_scenario_tooltip')}
          />
          <TextArea
            name="simulationScenario"
            label={translate('simulation_scenario')}
            labelHidden={true}
            required={true}
            autoComplete="off"
            info={errors.simulationScenario?.message}
            status={errors.simulationScenario?.message ? 'error' : undefined}
            ref={register({ required: translate('task_empty') })}
            resizable
            placeholder={translate('task_for_simulation')}
          />
        </Flex>
        <Flex
          container={{
            direction: 'column',
            gap: 1,
          }}
        >
          <CustomLabel
            label={`${translate('queue_name')} ${translate('optional')} `}
            tooltipInfo={translate('queue_name_tooltip')}
          />
          <Input
            name="queueName"
            label={`${translate('queue_name')} ${translate('optional')}`}
            autoComplete="off"
            labelHidden={true}
            type="text"
            info={errors.queueName?.message}
            status={errors.queueName?.message ? 'error' : undefined}
            ref={register}
          />
        </Flex>
        <Flex
          container={{
            direction: 'column',
            gap: 1,
          }}
        >
          <CustomLabel
            label={`${translate('personality')} ${translate('optional')} `}
            tooltipInfo={translate('personality_tooltip')}
          />
          <Input
            name="personality"
            label={`${translate('personality')} ${translate('optional')}`}
            autoComplete="off"
            labelHidden={true}
            type="text"
            info={errors.personality?.message}
            status={errors.personality?.message ? 'error' : undefined}
            ref={register}
          />
        </Flex>
        <Flex
          container={{
            direction: 'column',
            gap: 1,
          }}
        >
          <CustomLabel
            label={`${translate('pain_points')} ${translate('optional')} `}
            tooltipInfo={translate('pain_points_tooltip')}
          />
          <Input
            name="painPoints"
            label={`${translate('pain_points')} ${translate('optional')}`}
            autoComplete="off"
            labelHidden={true}
            type="text"
            info={errors.painPoints?.message}
            status={errors.painPoints?.message ? 'error' : undefined}
            ref={register}
          />
        </Flex>
      </Flex>
    </Form>
  );
};

export default AddTask;
